import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import {
  getGifts,
  requestNumerologyReport,
  type ReadingRecord,
} from '@/api/client';
import OrnateFrame from '@/components/OrnateFrame';
import RichText from '@/components/RichText';
import { useUserStore } from '@/store/userStore';
import { usePayment } from '@/hooks/usePayment';
import { useSubscription } from '@/hooks/useSubscription';

/**
 * Premium numerology report (100⭐). One-time purchase or free for
 * Priestess. The user can override their display name with a full
 * birth-certificate name for a more accurate reading.
 */
export default function Numerology() {
  const { t } = useTranslation();
  const user = useUserStore((s) => s.user);
  const [fullName, setFullName] = useState(
    user?.display_name ?? user?.first_name ?? '',
  );
  const [reading, setReading] = useState<ReadingRecord | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasGift, setHasGift] = useState(false);

  const { pay, paying, payError } = usePayment('numerology_report');
  const { isPriestess } = useSubscription();

  useEffect(() => {
    getGifts()
      .then((g) => setHasGift(g.products.includes('numerology_report')))
      .catch(() => {});
  }, []);

  const runReport = async () => {
    setError(null);
    try {
      const r = await requestNumerologyReport({ full_name: fullName.trim() });
      setReading(r);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const onSubmit = () => {
    if (busy || !fullName.trim()) return;
    if (isPriestess || hasGift) {
      setBusy(true);
      runReport().finally(() => setBusy(false));
      return;
    }
    pay(runReport);
  };

  const reset = () => {
    setReading(null);
    setError(null);
  };

  return (
    <main className="mx-auto max-w-md px-5 pb-24 pt-2">
      <h1 className="mb-1 text-center font-serif text-2xl text-gold-300">
        🔢 {t('numerology.title')}
      </h1>
      <p className="mb-6 text-center text-xs italic text-gold-300/60">
        {t('numerology.subtitle')}
      </p>

      <AnimatePresence mode="wait">
        {!reading ? (
          <motion.section
            key="form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            <OrnateFrame className="card-glow mb-4">
              <label className="block">
                <span className="mb-2 block text-[10px] uppercase tracking-widest text-gold-500/60">
                  {t('numerology.name_label')}
                </span>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={t('numerology.name_placeholder')}
                  maxLength={128}
                  className="w-full rounded-lg border border-gold-500/30
                             bg-night-900/60 px-3 py-2 font-serif text-base
                             text-gold-100 outline-none transition
                             placeholder:text-gold-300/35 focus:border-gold-400"
                />
                <p className="mt-1 text-[10px] italic text-gold-300/40">
                  {t('numerology.name_hint')}
                </p>
              </label>
            </OrnateFrame>

            <button
              type="button"
              onClick={onSubmit}
              disabled={!fullName.trim() || busy || paying}
              className="btn-primary w-full disabled:opacity-40"
            >
              {paying || busy
                ? t('payment.opening')
                : isPriestess
                  ? t('numerology.open_priestess')
                  : hasGift
                    ? t('numerology.open_gift')
                    : t('numerology.open_for_stars', { stars: 100 })}
            </button>
            {isPriestess && hasGift && (
              <div className="mt-2 space-y-0.5 text-center text-[10px] uppercase tracking-widest">
                <p className="text-gold-400/70">✦ {t('spread.included_priestess')}</p>
                <p className="text-emerald-300/80">🎁 {t('spread.gift_kept_for_later')}</p>
              </div>
            )}
            {isPriestess && !hasGift && (
              <p className="mt-2 text-center text-[10px] uppercase tracking-widest text-gold-400/70">
                ✦ {t('spread.included_priestess')}
              </p>
            )}
            {!isPriestess && hasGift && (
              <p className="mt-2 text-center text-[10px] uppercase tracking-widest text-emerald-300/80">
                🎁 {t('spread.gift_explain')}
              </p>
            )}
            {(payError || error) && (
              <p className="mt-3 text-center text-xs text-rose-400">
                {payError ?? error}
              </p>
            )}

            <p className="mt-5 text-center text-xs italic text-gold-300/50">
              {t('numerology.description')}
            </p>

            {/* What's inside teaser */}
            <ul className="mt-4 space-y-1.5 text-xs text-gold-300/70">
              {[
                'inside_life_path',
                'inside_destiny',
                'inside_soul_urge',
                'inside_personality',
                'inside_birthday',
                'inside_personal_year',
                'inside_personal_month',
                'inside_strengths',
                'inside_guidance',
              ].map((k) => (
                <li key={k} className="flex items-start gap-2">
                  <span className="text-gold-500/80">✦</span>
                  <span>{t(`numerology.${k}`)}</span>
                </li>
              ))}
            </ul>
          </motion.section>
        ) : (
          <motion.section
            key="result"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Cache-hit banner — quiet, just an "fyi" so the user understands
                why their text didn't regenerate. */}
            {reading.from_cache && (
              <div className="mb-3 rounded-lg border border-gold-500/30
                              bg-gold-500/10 px-3 py-2 text-[12px] leading-snug
                              text-gold-200">
                ✦ {t('numerology.from_cache')}
              </div>
            )}
            {/* Truncated banner — louder, with a retry button. Gift was
                already restored on the backend; clicking "Сгенерировать"
                triggers a fresh attempt for free. */}
            {reading.truncated && (
              <div className="mb-3 rounded-lg border border-rose-400/40
                              bg-rose-500/10 px-3 py-2.5 text-[12px] leading-snug
                              text-rose-200">
                <p className="mb-2">⚠ {t('numerology.truncated')}</p>
                <button
                  type="button"
                  onClick={() => { reset(); onSubmit(); }}
                  className="rounded-full border border-gold-500/40 bg-night-900/50
                             px-3 py-1 text-[11px] uppercase tracking-widest
                             text-gold-200 transition hover:border-gold-400"
                >
                  ↻ {t('numerology.retry')}
                </button>
              </div>
            )}

            <OrnateFrame className="card-glow mb-4">
              <RichText
                text={reading.interpretation}
                className="font-serif italic leading-relaxed text-gold-200"
              />
            </OrnateFrame>
            <div className="mt-4 flex items-center justify-between gap-3">
              <button type="button" onClick={reset} className="nav-back-pill">
                <span>←</span>
                <span>{t('numerology.back')}</span>
              </button>
              <button
                type="button"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                aria-label={t('reading.scroll_to_top')}
                className="nav-scroll-top"
              >
                ↑
              </button>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </main>
  );
}
